// formulary-change-alerts.js - Tier 1 Educational
export const content = {
  heroStyle: 'dark',
  tag: 'Plan Intelligence',

  html: `
<p>Of all the reasons clients leave their Medicare plans, formulary changes might be the most frustrating—because they're almost always preventable losses. A client's medication gets dropped or moved to a higher tier, they find out at the pharmacy, they panic, and suddenly they're shopping for a new plan.</p>

<p>The thing is, these changes are knowable in advance. Carriers announce formulary changes before they take effect. The information is out there. The question is whether you're catching it before your clients do.</p>

<h2>Why Formulary Changes Matter So Much</h2>

<p>Medications are personal. When someone's been taking the same drug for years and suddenly they can't get it—or it costs 5x more—that's not an abstract plan issue. That's their daily life being disrupted.</p>

<p>And it happens more often than most agents realize:</p>
<ul>
<li>Plans remove drugs from formularies entirely</li>
<li>Drugs move from preferred to non-preferred tiers (higher cost)</li>
<li>Prior authorization requirements get added</li>
<li>Step therapy requirements change what you try first</li>
<li>Quantity limits restrict how much you can get</li>
</ul>

<p>Any of these can make a client's current plan suddenly feel wrong. And when a client feels like their plan "doesn't cover their medications anymore," they're one competitor phone call away from switching.</p>

<h2>The Hidden Retention Killer</h2>

<p>Here's what makes formulary changes particularly dangerous for retention: clients often don't tell you about the problem.</p>

<p>They go to the pharmacy, get surprised by a higher copay or denial, feel frustrated and confused, and... don't call you. They just assume the plan is bad now. They start paying attention to those $0 premium mailers. By the time AEP rolls around, they've already decided to switch.</p>

<p>You never had a chance to explain, help, or solve the problem—because you didn't know it existed.</p>

<h2>Getting Ahead of Changes</h2>

<h3>1. Know When Formulary Updates Happen</h3>

<p>Most formulary changes take effect January 1, announced in the Annual Notice of Change (ANOC) sent in September. But plans can make mid-year changes too, typically with 30-60 days notice.</p>

<p>Key timing:</p>
<ul>
<li><strong>September-October:</strong> ANOC letters reveal next year's formulary changes</li>
<li><strong>Throughout the year:</strong> Watch for mid-year change notices</li>
<li><strong>Before AEP:</strong> Review formularies for next plan year</li>
</ul>

<h3>2. Track Your Clients' Key Medications</h3>

<p>You should know the important medications for each client—especially expensive ones, specialty drugs, or maintenance medications they take daily. This is the information that lets you check for problems proactively.</p>

<p>At minimum, know:</p>
<ul>
<li>Brand name drugs (higher cost, more likely to have changes)</li>
<li>Specialty medications (often require prior auth)</li>
<li>Controlled substances (subject to quantity limits)</li>
<li>New medications added in the past year</li>
</ul>

<h3>3. Review Formularies Before Clients Get Surprised</h3>

<p>When formulary changes are announced:</p>
<ul>
<li>Check if any of your clients' medications are affected</li>
<li>Identify the impact: dropped entirely? Tier change? New restrictions?</li>
<li>Determine alternatives: Is there a covered alternative? Exception process?</li>
</ul>

<p>Do this before your clients find out the hard way.</p>

<h3>4. Proactive Outreach</h3>

<p>When you identify an affected client, reach out immediately:</p>

<p><em>"Hey, I was reviewing your plan's drug coverage for next year and noticed a change that affects [medication]. I wanted to explain what's happening and talk through your options before you run into any issues."</em></p>

<p>This single call prevents the pharmacy surprise, positions you as proactive, and gives you a chance to either solve the problem or manage the transition.</p>

<h2>When Formulary Changes Happen</h2>

<h3>Options to Explore</h3>

<p>When a client's medication is affected, you have several paths:</p>

<p><strong>1. Formulary Exception Request</strong></p>
<p>Plans can make exceptions for medical necessity. If the client has tried alternatives and this specific medication is needed, a coverage determination request may get it covered at a better tier.</p>

<p><strong>2. Therapeutic Alternatives</strong></p>
<p>Is there a similar medication that IS covered? Work with the client's doctor to explore alternatives. Often there's a covered option that works just as well.</p>

<p><strong>3. Manufacturer Assistance</strong></p>
<p>Many drug manufacturers offer copay cards, patient assistance programs, or discount programs. These can offset the cost increase even if the plan coverage changed.</p>

<p><strong>4. Plan Change at AEP</strong></p>
<p>If none of the above work, a formulary change might be a legitimate reason to switch plans at AEP. Better you help them find a plan that covers their medications than lose them to another agent.</p>

<h2>The Pharmacy Relationship</h2>

<p>Pharmacists often know about formulary issues before agents do—they're the ones processing the prescriptions. Building a relationship with your clients' pharmacies can give you early warning:</p>

<ul>
<li>When a client has a claim rejected or high copay</li>
<li>When prior auth requirements kick in</li>
<li>When the pharmacy sees patterns of coverage issues</li>
</ul>

<p>Some agents ask clients to list them as a contact for coverage issues. Others simply ask clients to call them if anything weird happens at the pharmacy.</p>

<h2>Documentation and Tracking</h2>

<p>For each client, you should be tracking:</p>
<ul>
<li>Current medications (at least the important ones)</li>
<li>Current formulary tier for each medication</li>
<li>Any known coverage issues or exceptions in place</li>
<li>Changes announced for the upcoming year</li>
</ul>

<p>This documentation lets you be proactive instead of reactive. When formulary updates are announced, you can quickly identify which clients are affected.</p>

<h2>The Bigger Picture</h2>

<p>Formulary changes are one example of a broader principle: <strong>know about changes that affect your clients before they do.</strong></p>

<p>The same applies to network changes, benefit modifications, premium increases—anything that alters the client's experience with their plan. The agents who retain best are the ones who catch these changes first and reach out proactively.</p>

<p>Your clients shouldn't be learning about plan changes from a pharmacy rejection or a carrier letter. They should be learning from you, with context and solutions already in hand.</p>

<p>That's what separates agents who retain from agents who wonder why clients left.</p>
  `,

  faqs: [
    {
      question: "When do Medicare formulary changes typically happen?",
      answer: "Most changes take effect January 1 and are announced in the ANOC (Annual Notice of Change) sent in September. However, plans can make mid-year changes with 30-60 days notice. Watch for both annual and mid-year formulary updates."
    },
    {
      question: "What should I do when a client's medication is dropped from their plan's formulary?",
      answer: "Explore options in order: 1) Formulary exception request for medical necessity, 2) Therapeutic alternatives that are covered, 3) Manufacturer assistance programs, 4) If none work, help them find a better-fitting plan at AEP. Always communicate proactively before they find out at the pharmacy."
    },
    {
      question: "How do I track formulary changes for my clients?",
      answer: "Know each client's key medications (especially brand names, specialty drugs, controlled substances). When formulary updates are announced, check affected drug lists against your client records. This lets you proactively identify who's impacted before they find out."
    },
    {
      question: "What are the common types of formulary changes that affect clients?",
      answer: "Drugs removed from formulary entirely, tier changes (preferred to non-preferred = higher cost), new prior authorization requirements, step therapy requirements (try other drugs first), and quantity limits. Each impacts the client differently."
    },
    {
      question: "Why don't clients tell me when they have prescription problems?",
      answer: "Many clients don't realize their agent can help with prescription issues, or they're embarrassed about the situation, or they just assume the plan is bad now. They don't call—they just decide to switch plans. This is why proactive monitoring matters."
    }
  ],

  sources: [
    {
      title: "Medicare Part D Formulary Guidance",
      url: "https://www.cms.gov/Medicare/Prescription-Drug-Coverage/PrescriptionDrugCovContra",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Formulary changes are knowable in advance—but only if you're tracking them. Pro IRP monitors plan formulary updates and alerts you when your clients' medications are affected, so you can reach out before they find out at the pharmacy.`
};

export default content;
